// confetti-canvas.ts
export type ConfettiCanvas = {
  canvas: HTMLCanvasElement | null
  ctx: CanvasRenderingContext2D | null
}

/**
 * Ensures the confetti canvas and its styles exist as the last children of <body>,
 * then returns the typed canvas + 2D context.
 *
 * Call this after the DOM is ready (e.g., in a useEffect, on DOMContentLoaded, or after window.onload).
 */
export function ensureConfettiCanvas(): ConfettiCanvas {
  if (typeof document === 'undefined' || !document.body) {
    return { canvas: null, ctx: null }
  }

  // 1) Create/ensure the <canvas id="confettiCanvas">
  let canvas = document.getElementById('confettiCanvas') as HTMLCanvasElement | null
  if (!canvas) {
    canvas = document.createElement('canvas')
    canvas.id = 'confettiCanvas'
    document.body.appendChild(canvas) // appends just before </body>
  }

  // 2) Create/ensure the inline style tag for the canvas
  const styleId = 'confettiCanvas-inline-style'
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style')
    style.id = styleId
    style.textContent = `
      #confettiCanvas {
        position: fixed;
        inset: 0;
        width: 100%;
        height: 100%;
        pointer-events: none;
        z-index: 9999;
      }
    `.trim()
    document.body.appendChild(style) // appends just before </body>
  }

  // 3) Get the 2D context (typed)
  const ctx = canvas.getContext('2d')

  return { canvas, ctx }
}

const { canvas, ctx } = ensureConfettiCanvas()

// const canvas = document.getElementById('confettiCanvas') as HTMLCanvasElement | null;
// const ctx = canvas?.getContext('2d') ?? null;
console.log('CONFETTI', canvas)

// Handle high-DPI and resize
const resize = (): void => {
  if (!canvas || !ctx) return
  const dpr = Math.max(1, window.devicePixelRatio || 1)
  const { innerWidth: w, innerHeight: h } = window
  canvas.width = Math.floor(w * dpr)
  canvas.height = Math.floor(h * dpr)
  canvas.style.width = w + 'px'
  canvas.style.height = h + 'px'
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
}

if (typeof window !== 'undefined') {
  window.addEventListener('resize', resize, { passive: true })
  resize()
}

// Particle system
const TAU = Math.PI * 2
const COLORS = ['#f59e0b', '#10b981', '#3b82f6'] as const

const particles = new Set<Confetti>()
let rafId: number | null = null

interface ConfettiOptions {
  angle?: number
  speed?: number
  spread?: number
}

class Confetti {
  x: number
  y: number
  vx: number
  vy: number
  w: number
  h: number
  color: string
  rotation: number
  rotationSpeed: number
  opacity: number
  drag: number
  gravity: number
  life: number
  spinFlip: number

  constructor(x: number, y: number, options: ConfettiOptions = {}) {
    const angle = options.angle ?? Math.random() * TAU
    const speed = options.speed ?? 6 + Math.random() * 6
    const spread = options.spread ?? TAU / 4
    const a = angle + (Math.random() - 0.5) * spread

    this.x = x
    this.y = y
    this.vx = Math.cos(a) * speed
    this.vy = Math.sin(a) * speed - 6 * Math.random() // initial upward kick
    this.w = 6 + Math.random() * 6
    this.h = 10 + Math.random() * 10
    this.color = COLORS[(Math.random() * COLORS.length) | 0]
    this.rotation = Math.random() * TAU
    this.rotationSpeed = (Math.random() - 0.5) * 0.3
    this.opacity = 1
    this.drag = 0.998 // air resistance
    this.gravity = 0.15 // downward force
    this.life = 80 + Math.random() * 40 // frames
    this.spinFlip = Math.random() < 0.5 ? 1 : -1 // simulate flutter
  }

  step(): boolean {
    this.vx *= this.drag
    this.vy = this.vy * this.drag + this.gravity
    this.x += this.vx
    this.y += this.vy

    this.rotation += this.rotationSpeed
    // Flutter by flipping height scale
    this.spinFlip *= 0.98
    this.life -= 1
    if (this.life < 20) this.opacity = Math.max(0, this.life / 20)
    return this.opacity > 0 && this.y < window.innerHeight + 50 && this.x > -50 && this.x < window.innerWidth + 50
  }

  draw(ctx: CanvasRenderingContext2D): void {
    ctx.save()
    ctx.globalAlpha = this.opacity
    ctx.translate(this.x, this.y)
    ctx.rotate(this.rotation)
    ctx.fillStyle = this.color
    ctx.fillRect(-this.w / 2, (-this.h / 2) * this.spinFlip, this.w, this.h)
    ctx.restore()
  }
}

function spawnConfetti(x: number, y: number, count = 120): void {
  for (let i = 0; i < count; i++) {
    particles.add(
      new Confetti(x, y, {
        angle: -Math.PI / 2, // mostly upwards
        spread: Math.PI, // 180° spread
        speed: 7 + Math.random() * 7,
      }),
    )
  }
  startLoop()
}

function loop(): void {
  if (!canvas || !ctx) return

  ctx.clearRect(0, 0, canvas.width, canvas.height)
  for (const p of particles) {
    if (!p.step()) particles.delete(p)
    else p.draw(ctx)
  }
  if (particles.size) {
    rafId = window.requestAnimationFrame(loop)
  } else if (rafId != null) {
    window.cancelAnimationFrame(rafId)
    rafId = null
  }
}

function startLoop(): void {
  if (rafId == null) rafId = window.requestAnimationFrame(loop)
}

function getButtonCenter(el: Element): { x: number; y: number } {
  const r = el.getBoundingClientRect()
  return { x: r.left + r.width / 2, y: r.top + r.height / 2 }
}

// Public API
export const doConfetti = (element: Element | null): void => {
  if (!element) return
  if (!canvas || !ctx) {
    return
  }
  const counts = [80, 120, 150, 200]
  const randomCount = counts[Math.floor(Math.random() * counts.length)]
  const { x, y } = getButtonCenter(element)
  spawnConfetti(x, y, randomCount)
}
